/**
 * Auto-generated large UPS names — serial-style hash (e.g. Large-UPS-102H33).
 * Used when the user adds/saves/duplicates a unit without providing a custom name.
 *
 * Note: the server auto-names omitted units "Large UPS N" by display order.
 * This client helper only synthesizes unique names for the duplicate flow (mirrors
 * src/utils/coolingSystemAutoName.ts).
 */

export const AUTO_LARGE_UPS_NAME_PREFIX = 'Large-UPS-';

const AUTO_SERIAL_BODY = /^[A-Z0-9]{5,8}$/i;
const AUTO_COLLISION_SUFFIX = /^-\d+$/;

export function isAutoGeneratedLargeUpsName(name: string | undefined): boolean {
  const trimmed = name?.trim();
  if (!trimmed?.startsWith(AUTO_LARGE_UPS_NAME_PREFIX)) {
    return false;
  }
  const rest = trimmed.slice(AUTO_LARGE_UPS_NAME_PREFIX.length);
  const dashIdx = rest.lastIndexOf('-');
  if (dashIdx > 0 && AUTO_COLLISION_SUFFIX.test(rest.slice(dashIdx))) {
    return AUTO_SERIAL_BODY.test(rest.slice(0, dashIdx));
  }
  return AUTO_SERIAL_BODY.test(rest);
}

function largeUpsAutoNameSerial(seed: number): string {
  const abs = Math.abs(seed);
  const base36 = abs.toString(36).toUpperCase();
  const serial = base36.slice(-6);
  return serial.length >= 5 ? serial : serial.padStart(5, '0');
}

function normalizeTakenNames(
  takenNames: Iterable<string | undefined>,
): Set<string> {
  return new Set(
    [...takenNames]
      .map(name => name?.trim().toLowerCase())
      .filter((name): name is string => Boolean(name)),
  );
}

/** Unique serial name from a numeric seed (typically temp id or timestamp). */
export function generateAutoLargeUpsName(
  seed: number,
  takenNames: Iterable<string | undefined> = [],
): string {
  const taken = normalizeTakenNames(takenNames);
  const base = `${AUTO_LARGE_UPS_NAME_PREFIX}${largeUpsAutoNameSerial(seed)}`;

  if (!taken.has(base.toLowerCase())) {
    return base;
  }

  let suffix = 2;
  while (taken.has(`${base}-${suffix}`.toLowerCase())) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}
